// ===== SHOP THE LOOK WEB COMPONENTS - SELECTORES ÚNICOS =====

// Clase base para utilidades comunes
class ShopTheLookBase extends HTMLElement {
    constructor() {
        super();
        this.initialized = false;
    }

    // Throttle function para optimizar eventos
    throttle(func, delay = 100) {
        let timeoutId;
        let lastExecTime = 0;
        return (...args) => {
            const currentTime = Date.now();
            if (currentTime - lastExecTime > delay) {
                func.apply(this, args);
                lastExecTime = currentTime;
            } else {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    func.apply(this, args);
                    lastExecTime = Date.now();
                }, delay - (currentTime - lastExecTime));
            }
        };
    }

    // Dispatcher de eventos personalizados
    dispatch(eventName, detail = {}) {
        this.dispatchEvent(new CustomEvent(eventName, {
            bubbles: true,
            detail
        }));
    }
}

// ===== LOOKBOOK ELEMENT =====
class LookbookElement extends ShopTheLookBase {
    constructor() {
        super();
        this.hotspots = [];
        this.activeIndex = 0;
    }

    connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        // Pequeño delay para asegurar que el DOM esté listo
        setTimeout(() => this.init(), 100);
    }

    init() {
        // Usar selectores específicos para evitar conflictos
        this.hotspots = Array.from(this.querySelectorAll('.stl-hotspot, .hotspot'));

        if (this.hotspots.length === 0) return;

        this.setupEventListeners();
        this.setInitialState();
    }

    setupEventListeners() {
        this.hotspots.forEach((hotspot, index) => {
            // Eventos de mouse
            hotspot.addEventListener('mouseenter', () => this.activateHotspot(index));
            hotspot.addEventListener('mouseleave', () => this.deactivateHotspot(index));
            hotspot.addEventListener('click', (e) => this.handleHotspotClick(e, index));

            // Eventos de touch para móviles
            hotspot.addEventListener('touchstart', (e) => this.handleTouchStart(e, index));

            // Eventos de teclado
            hotspot.addEventListener('keydown', (e) => this.handleKeyDown(e, index));

            // Hacer que los hotspots sean focusables
            hotspot.setAttribute('tabindex', index === 0 ? '0' : '-1');
            hotspot.setAttribute('role', 'button');
            hotspot.setAttribute('aria-label', `Ver producto ${index + 1}`);
        });

        // Escuchar eventos del carrusel usando namespace específico
        document.addEventListener('stl-carousel:change', (e) => {
            if (this.isConnectedToCarousel(e.target)) {
                this.selectHotspot(e.detail.index, false);
            }
        });
    }

    setInitialState() {
        this.selectHotspot(0, false);
    }

    activateHotspot(index) {
        if (this.activeIndex !== index) {
            this.selectHotspot(index, true);
        }
    }

    deactivateHotspot(index) {
        // En hover out, no desactivamos automáticamente
        // Solo limpiamos estados temporales
    }

    handleHotspotClick(event, index) {
        event.preventDefault();
        this.selectHotspot(index, true);
        this.focusHotspot(index);
    }

    handleTouchStart(event, index) {
        // Para móviles, activar inmediatamente
        this.selectHotspot(index, true);
    }

    handleKeyDown(event, index) {
        switch (event.key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.selectHotspot(index, true);
                break;
            case 'ArrowRight':
                event.preventDefault();
                this.navigateToNext();
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.navigateToPrevious();
                break;
            case 'Home':
                event.preventDefault();
                this.navigateToFirst();
                break;
            case 'End':
                event.preventDefault();
                this.navigateToLast();
                break;
        }
    }

    selectHotspot(index, triggerCarousel = true) {
        if (index < 0 || index >= this.hotspots.length) return;

        // Remover estado activo de todos los hotspots usando ambas clases
        this.hotspots.forEach((hotspot, i) => {
            // Usar clase específica stl-active y fallback active
            hotspot.classList.toggle('stl-active', i === index);
            hotspot.classList.toggle('active', i === index);
            hotspot.setAttribute('aria-current', i === index ? 'true' : 'false');
            hotspot.setAttribute('tabindex', i === index ? '0' : '-1');
        });

        this.activeIndex = index;

        // Notificar al carrusel si es necesario usando namespace específico
        if (triggerCarousel) {
            this.dispatch('stl-lookbook:change', { index });
        }
    }

    focusHotspot(index) {
        if (this.hotspots[index]) {
            this.hotspots[index].focus();
        }
    }

    navigateToNext() {
        const nextIndex = (this.activeIndex + 1) % this.hotspots.length;
        this.selectHotspot(nextIndex, true);
        this.focusHotspot(nextIndex);
    }

    navigateToPrevious() {
        const prevIndex = (this.activeIndex - 1 + this.hotspots.length) % this.hotspots.length;
        this.selectHotspot(prevIndex, true);
        this.focusHotspot(prevIndex);
    }

    navigateToFirst() {
        this.selectHotspot(0, true);
        this.focusHotspot(0);
    }

    navigateToLast() {
        const lastIndex = this.hotspots.length - 1;
        this.selectHotspot(lastIndex, true);
        this.focusHotspot(lastIndex);
    }

    isConnectedToCarousel(carouselElement) {
        return carouselElement && carouselElement.closest('shop-the-look') === this.closest('shop-the-look');
    }

    // API pública
    getCurrentIndex() {
        return this.activeIndex;
    }

    getTotalHotspots() {
        return this.hotspots.length;
    }
}

// ===== CAROUSEL ELEMENT =====
class CarouselElement extends ShopTheLookBase {
    constructor() {
        super();
        this.slides = [];
        this.currentIndex = 0;
        this.isAnimating = false;
        this.mobileItemsPerSlide = 1;  
        this.itemsPerSlide = 1;       
        this.maxIndex = 0;
    }

    connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        setTimeout(() => this.init(), 100);
    }

    init() {
        this.slides = Array.from(this.children);

        if (this.slides.length === 0) return;

        this.mobileItemsPerSlide = parseInt(
            this.getAttribute('data-mobile-products-per-slide') || '1',
            10
        );

        this.updateItemsPerSlide();
        this.createCarouselStructure();
        this.setupEventListeners();
        this.setInitialState();
        this.setupAutoplay();

        window.addEventListener(
            'resize',
            this.throttle(() => {
                const oldItems = this.itemsPerSlide;
                this.updateItemsPerSlide();
                if (oldItems !== this.itemsPerSlide) {
                this.refreshIndicators();
                // volvemos al primer grupo para evitar desajustes
                this.goToSlide(0, false);
                }
            }, 200)
        );
    }

    updateItemsPerSlide() {
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        this.itemsPerSlide = isMobile ? this.mobileItemsPerSlide : 1;
        this.maxIndex = Math.ceil(this.slides.length / this.itemsPerSlide) - 1;
    }
    

    createCarouselStructure() {
        // Crear contenedor del carrusel con clase específica
        this.container = document.createElement('div');
        this.container.className = 'stl-carousel-container';

        // Crear track con clase específica
        this.track = document.createElement('div');
        this.track.className = 'stl-carousel-track';

        // Mover slides al track con clase específica
        this.slides.forEach((slide, index) => {
            const slideWrapper = document.createElement('div');
            slideWrapper.className = 'stl-carousel-slide';
            slideWrapper.appendChild(slide);
            this.track.appendChild(slideWrapper);
        });

        this.container.appendChild(this.track);
        this.appendChild(this.container);

        // Crear navegación si hay más de un slide
        if (this.slides.length > 1) {
            this.createNavigation();
            this.createIndicators();
        }
    }

    createNavigation() {
        // Botón anterior con clases específicas
        this.prevButton = document.createElement('button');
        this.prevButton.className = 'stl-carousel-nav stl-carousel-nav--prev';
        this.prevButton.innerHTML = `
      <svg class="stl-icon" viewBox="0 0 24 24" fill="none">
        <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
        this.prevButton.setAttribute('aria-label', 'Producto anterior');

        // Botón siguiente con clases específicas
        this.nextButton = document.createElement('button');
        this.nextButton.className = 'stl-carousel-nav stl-carousel-nav--next';
        this.nextButton.innerHTML = `
      <svg class="stl-icon" viewBox="0 0 24 24" fill="none">
        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
        this.nextButton.setAttribute('aria-label', 'Producto siguiente');

        this.container.appendChild(this.prevButton);
        this.container.appendChild(this.nextButton);

        // Event listeners
        this.prevButton.addEventListener('click', () => this.previous());
        this.nextButton.addEventListener('click', () => this.next());
    }

    createIndicators() {
        // Contenedor con clase específica
        this.indicatorsContainer = document.createElement('div');
        this.indicatorsContainer.className = 'stl-carousel-indicators';

        this.indicators = [];
        for (let index = 0; index <= this.maxIndex; index++) {
            const indicator = document.createElement('button');
            indicator.className = 'stl-carousel-indicator';
            indicator.setAttribute('aria-label', `Ir al grupo ${index + 1}`);
            indicator.addEventListener('click', () => this.goToSlide(index));

            this.indicators.push(indicator);
            this.indicatorsContainer.appendChild(indicator);
        }
        

        this.appendChild(this.indicatorsContainer);
    }
    refreshIndicators() {
        if (this.indicatorsContainer) this.indicatorsContainer.remove();
        this.createIndicators();
    }

    setupEventListeners() {
        // Escuchar eventos del lookbook usando namespace específico
        document.addEventListener('stl-lookbook:change', (e) => {
            if (this.isConnectedToLookbook(e.target)) {
                const group = Math.floor(e.detail.index / this.itemsPerSlide);
                this.goToSlide(group, false);
            }
        });

        // Pausar autoplay en hover
        this.addEventListener('mouseenter', () => this.pauseAutoplay());
        this.addEventListener('mouseleave', () => this.resumeAutoplay());

        // Soporte para gestos touch
        this.setupTouchEvents();

        // Navegación por teclado
        this.addEventListener('keydown', (e) => this.handleKeyDown(e));
        this.setAttribute('tabindex', '0');
    }

    setupTouchEvents() {
        let touchStartX = 0;
        let touchEndX = 0;

        this.addEventListener('touchstart', (e) => {
            touchStartX = e.changedTouches[0].screenX;
        });

        this.addEventListener('touchend', (e) => {
            touchEndX = e.changedTouches[0].screenX;
            this.handleSwipe();
        });

        this.handleSwipe = () => {
            const swipeThreshold = 50;
            const diff = touchStartX - touchEndX;

            if (Math.abs(diff) > swipeThreshold) {
                if (diff > 0) {
                    this.next(); // Swipe left - next
                } else {
                    this.previous(); // Swipe right - previous
                }
            }
        };
    }

    handleKeyDown(event) {
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                this.previous();
                break;
            case 'ArrowRight':
                event.preventDefault();
                this.next();
                break;
            case 'Home':
                event.preventDefault();
                this.goToSlide(0);
                break;
            case 'End':
                event.preventDefault();
                this.goToSlide(this.maxIndex);
                break;
        }
    }

    setInitialState() {
        this.updateCarousel(0, true);
    }

    setupAutoplay() {
        const autoplay = this.getAttribute('data-autoplay');
        if (autoplay === 'true' && this.slides.length > 1) {
            this.startAutoplay();
        }
    }

    startAutoplay() {
        this.stopAutoplay();
        this.autoplayInterval = setInterval(() => {
            this.next();
        }, this.autoplayDelay);
    }

    stopAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    pauseAutoplay() {
        this.stopAutoplay();
    }

    resumeAutoplay() {
        const autoplay = this.getAttribute('data-autoplay');
        if (autoplay === 'true') {
            this.startAutoplay();
        }
    }

    goToSlide(index, triggerLookbook = true) {
        if (index < 0 || index > this.maxIndex || this.isAnimating) return;

        this.updateCarousel(index);

        if (triggerLookbook) {
            // Usar namespace específico para evitar conflictos
            this.dispatch('stl-carousel:change', {
                index: index * this.itemsPerSlide, 
                previousIndex: this.currentIndex
            });    
        }

        this.currentIndex = index;
    }

    next() {
        const nextIndex = (this.currentIndex + 1) % (this.maxIndex + 1);
        this.goToSlide(nextIndex);
    }

    previous() {
        const prevIndex = (this.currentIndex - 1 + (this.maxIndex + 1)) % (this.maxIndex + 1);
        this.goToSlide(prevIndex);
    }

    updateCarousel(index, immediate = false) {
        if (!this.track) return;

        this.isAnimating = !immediate;

        // Actualizar posición del track
        const translateX = -index * 100;

        if (immediate) {
            this.track.style.transition = 'none';
        } else {
            this.track.style.transition = 'transform 0.3s ease';
        }

        this.track.style.transform = `translateX(${translateX}%)`;

        // Actualizar botones de navegación
        this.updateNavigationButtons(index);

        // Actualizar indicadores
        this.updateIndicators(index);

        // Reset animation flag
        if (!immediate) {
            setTimeout(() => {
                this.isAnimating = false;
            }, 300);
        } else {
            this.isAnimating = false;
        }
    }

    updateNavigationButtons(index) {
        if (!this.prevButton || !this.nextButton) return;

        // En un carrusel infinito, los botones siempre están habilitados
        // Si no quieres infinito, puedes cambiar esta lógica
        this.prevButton.disabled = false;
        this.nextButton.disabled = false;
    }

    updateIndicators(index) {
        if (!this.indicators) return;

        this.indicators.forEach((indicator, i) => {
            // Usar clase específica stl-active
            indicator.classList.toggle('stl-active', i === index);
            indicator.classList.toggle('active', i === index); // Fallback
        });
    }

    isConnectedToLookbook(lookbookElement) {
        return lookbookElement && lookbookElement.closest('shop-the-look') === this.closest('shop-the-look');
    }

    disconnectedCallback() {
        this.stopAutoplay();
    }

    // API pública
    getCurrentIndex() {
        return this.currentIndex;
    }

    getTotalSlides() {
        return this.maxIndex + 1;
    }
}

// ===== SHOP THE LOOK COORDINATOR =====
class ShopTheLook extends ShopTheLookBase {
    constructor() {
        super();
        this.lookbook = null;
        this.carousel = null;
    }

    connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        // Esperar a que los elementos hijos estén inicializados
        setTimeout(() => this.init(), 200);
    }

    init() {
        this.lookbook = this.querySelector('lookbook-element');
        this.carousel = this.querySelector('carousel-element');

        if (!this.lookbook || !this.carousel) {
            console.warn('Shop-the-look: Missing lookbook or carousel element');
            return;
        }

        this.setupCoordination();
        this.setupGlobalEvents();

         const addAll = this.querySelector('.stl-add-all');
        if (addAll) {
        const carouselContainer = this.carousel.container || this.carousel;
        carouselContainer.parentNode.insertBefore(addAll, carouselContainer.nextSibling);
        }

        // Agregar clase de inicialización específica
        this.classList.add('stl-initialized');
    }

    setupCoordination() {
        // Los eventos ya están configurados en los elementos individuales
        // Aquí podemos agregar lógica adicional de coordinación si es necesaria

        // Sincronizar estados iniciales
        setTimeout(() => {
            const initialIndex = 0;
            if (this.lookbook) this.lookbook.selectHotspot(initialIndex, false);
            if (this.carousel) this.carousel.goToSlide(initialIndex, false);
        }, 100);
    }

    setupGlobalEvents() {
        // Escuchar eventos globales de la sección usando namespaces específicos
        this.addEventListener('stl-lookbook:change', (e) => {
            this.handleLookbookChange(e.detail);
        });

        this.addEventListener('stl-carousel:change', (e) => {
            this.handleCarouselChange(e.detail);
        });

        // Eventos de teclado global
        this.addEventListener('keydown', (e) => this.handleGlobalKeyDown(e));

        // Hacer focusable
        this.setAttribute('tabindex', '-1');
    }

    handleLookbookChange(detail) {
        // Agregar efectos adicionales cuando cambie el lookbook
        this.dispatch('stl-shop-the-look:change', detail);
    }

    handleCarouselChange(detail) {
        // Agregar efectos adicionales cuando cambie el carrusel
        this.dispatch('stl-shop-the-look:change', detail);
    }

    handleGlobalKeyDown(event) {
        // Manejar atajos de teclado globales si es necesario
        if (event.ctrlKey || event.metaKey) {
            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    this.previous();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    this.next();
                    break;
            }
        }
    }

    // API pública
    next() {
        if (this.carousel) {
            this.carousel.next();
        }
    }

    previous() {
        if (this.carousel) {
            this.carousel.previous();
        }
    }

    goToIndex(index) {
        if (this.lookbook) {
            this.lookbook.selectHotspot(index, true);
        }
    }

    getCurrentIndex() {
        return this.carousel ? this.carousel.getCurrentIndex() : 0;
    }

    getTotalItems() {
        return this.carousel ? this.carousel.getTotalSlides() : 0;
    }

    // Método para aplicar estado de carga específico
    setLoading(isLoading) {
        this.classList.toggle('stl-loading', isLoading);
    }
}

// ===== REGISTRAR WEB COMPONENTS =====
if (!customElements.get('lookbook-element')) {
    customElements.define('lookbook-element', LookbookElement);
}

if (!customElements.get('carousel-element')) {
    customElements.define('carousel-element', CarouselElement);
}

if (!customElements.get('shop-the-look')) {
    customElements.define('shop-the-look', ShopTheLook);
}

// ===== UTILIDADES GLOBALES ESPECÍFICAS =====

// Función para inicializar componentes manualmente con namespace específico
window.initShopTheLookSTL = function (selector = 'shop-the-look') {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
        if (!element.initialized) {
            element.init();
        }
    });
};

// Event listener para Shopify Theme Editor con namespace específico
if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
        const section = event.target;
        const shopTheLook = section.querySelector('shop-the-look');
        if (shopTheLook) {
            setTimeout(() => {
                if (shopTheLook.init) {
                    shopTheLook.init();
                }
            }, 100);
        }
    });

    document.addEventListener('shopify:block:select', (event) => {
        const block = event.target;
        // Buscar tanto clases específicas como fallback
        const hotspot = block.closest('.stl-hotspot') || block.closest('.hotspot');
        if (hotspot) {
            const lookbook = hotspot.closest('lookbook-element');
            if (lookbook) {
                const hotspots = Array.from(lookbook.querySelectorAll('.stl-hotspot, .hotspot'));
                const index = hotspots.indexOf(hotspot);
                if (index >= 0 && lookbook.selectHotspot) {
                    lookbook.selectHotspot(index, true);
                }
            }
        }
    });
}

// Función de utilidad para limpiar eventos globales específicos del componente
window.cleanupShopTheLookSTL = function () {
    // Limpiar event listeners globales si es necesario
    const events = ['stl-lookbook:change', 'stl-carousel:change', 'stl-shop-the-look:change'];
    events.forEach(eventName => {
        document.removeEventListener(eventName, () => { });
    });
};

// Debugging específico (remover en producción)
if (typeof console !== 'undefined' && console.log) {
    console.log('Shop-the-Look STL web components loaded successfully');
}

// Export para módulos ES6 si es necesario con namespace específico
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        STLLookbookElement: LookbookElement,
        STLCarouselElement: CarouselElement,
        STLShopTheLook: ShopTheLook
    };
}
